import { ref } from 'vue'
import { defineStore } from 'pinia'
// @ts-expect-error: vuex is not typed. It will be migrated to typed pinia in the future.
import { useStore } from 'vuex'

import volunteerApi from '@/services/http/api/volunteer'
import { useChildrenFilterStore } from '@/stores/filterChildren'
import type {
  AssignVolunteerPayload,
  ChildEditionId,
  OpenVolunteerModalPayload,
  VolunteerListItem,
  VolunteerOption
} from '@/types'

export const useVolunteerStore = defineStore('volunteer', () => {
  const vuexStore = useStore()

  const volunteers = ref<VolunteerOption[]>([])
  const volunteerId = ref<string>()
  const volunteerModalVisibility = ref<boolean>(false)
  const selectedChildEditionId = ref<ChildEditionId | null>(null)
  const wioStructLocationName = ref<string | null>(null)
  const wioStructLocationId = ref<ChildEditionId | null>(null)
  const volunteersLoader = ref<boolean>(false)
  const sendingLoader = ref<boolean>(false)

  const clear = () => {
    volunteers.value = []
    volunteerId.value = undefined
    volunteerModalVisibility.value = false
    selectedChildEditionId.value = null
    wioStructLocationName.value = null
    wioStructLocationId.value = null
    volunteersLoader.value = false
    sendingLoader.value = false
  }

  const openModal = (payload: OpenVolunteerModalPayload) => {
    volunteerId.value = payload.volunteerId ? String(payload.volunteerId) : undefined
    selectedChildEditionId.value = payload.selectedChildEditionId ?? null
    wioStructLocationName.value = payload.wioStructLocationName ?? null
    wioStructLocationId.value = payload.wioStructLocationId ?? null
    volunteerModalVisibility.value = true
  }

  const updateChildEdition = (childEdition: unknown) => {
    const { school_hydra_users: schoolHydraUsers } =
      vuexStore.getters['childEdition/getChild'] ?? {}
    vuexStore.commit('childEdition/setChild', {
      child_edition: childEdition,
      school_hydra_users: schoolHydraUsers
    })
  }

  const getVolunteers = async (wioStructLocationId: ChildEditionId) => {
    volunteersLoader.value = true
    try {
      const { data } = await volunteerApi.getVolunteers(wioStructLocationId)
      volunteers.value = data.data.map((item: VolunteerListItem) => ({
        value: item.id,
        text: item.first_name.concat(' ', item.last_name)
      }))
    } catch (error) {
      console.error('Failed to fetch volunteers', error)
      throw error
    } finally {
      volunteersLoader.value = false
    }
  }

  const assignVolunteer = async (payload: AssignVolunteerPayload) => {
    sendingLoader.value = true
    try {
      const { data } = await volunteerApi.assignVolunteer(payload)

      if (payload.routeName === 'ChildCard') {
        updateChildEdition(data.data)
      }
      if (payload.routeName === 'ChildrenList') {
        const childrenFilterStore = useChildrenFilterStore()
        childrenFilterStore.updateChildrenListItem(data.data)
      }
      clear()
    } catch (error) {
      console.error('Failed to assign volunteer', error)
      throw error
    } finally {
      sendingLoader.value = false
    }
  }

  const detachVolunteer = async (childEditionId: ChildEditionId) => {
    sendingLoader.value = true
    try {
      const { data } = await volunteerApi.detachVolunteer(childEditionId)
      updateChildEdition(data.data)
      clear()
    } catch (error) {
      console.error('Failed to detach volunteer', error)
      throw error
    } finally {
      sendingLoader.value = false
    }
  }

  return {
    volunteers,
    volunteerId,
    volunteerModalVisibility,
    selectedChildEditionId,
    wioStructLocationName,
    wioStructLocationId,
    volunteersLoader,
    sendingLoader,

    openModal,
    clear,
    getVolunteers,
    assignVolunteer,
    detachVolunteer
  }
})
